import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Download, Copy, Check, ChevronDown, ChevronRight, Zap, Calendar as CalIcon,
    Film, BookOpen, Sparkles, Image as ImageIcon, ArrowDown, Instagram as InstagramIcon,
} from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import SystemVersionBadge from "@/components/SystemVersionBadge";
import DayCard from "./kit/DayCard";
import ReelsStrip from "./kit/ReelsStrip";
import { IntroBlock } from "./kit/IntroBlock";
import { ChannelBlock } from "./kit/ChannelBlock";
import { MediaPendingBanner } from "./kit/MediaPendingBanner";
import { absUrl, saveImage, isMobile, openInstagramCreate } from "./kit/_helpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---------- Helpers ----------
// Most helpers (absUrl, saveImage, isMobile, openInstagramCreate) live in
// `./kit/_helpers.js` so DayCard and ReelsStrip can share them. Local helpers
// below stay here because they're only used inside this file.

// Deterministic fake IG stats from cafe name (so it doesn't change on every render)
function fakeStats(cafe) {
    const seed = (cafe || "").split("").reduce((a, c) => a + c.charCodeAt(0), 7);
    return {
        posts: 230 + (seed * 47) % 70,
        followers: (1500 + (seed * 137) % 1800).toLocaleString(),
        following: 180 + (seed * 43) % 200,
    };
}

// Normalize an IG handle that might be entered as @user, user, or full URL.
// Returns just the username, no '@'.
function cleanIgHandle(raw) {
    if (!raw) return "";
    let s = String(raw).trim();
    s = s.replace(/^https?:\/\/(www\.)?instagram\.com\//i, "");
    s = s.replace(/^@/, "").replace(/\/$/, "").split("?")[0].split("/")[0];
    return s;
}

// ---------- Top bar: compact export options ----------
function TopExportBar({ orderId, hasMedia, hasImages, calendarLocked }) {
    return (
        <div
            className="sticky top-[64px] z-30 bg-cafe-cream/95 backdrop-blur-sm border-b border-border"
            data-testid="export-bar"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-2.5 flex items-center justify-between gap-3 text-xs">
                <span className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold hidden sm:block">
                    Export
                </span>
                <div className="flex items-center gap-2 flex-1 sm:flex-none justify-end">
                    {hasImages && (
                        <a
                            href={`${API}/kit/${orderId}/images.zip`}
                            data-testid="top-all-images-btn"
                            title="Download every AI image in one ZIP file — no more saving one by one."
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cafe-espresso hover:bg-[#1A0F0A] text-cafe-cream font-medium whitespace-nowrap transition-colors"
                        >
                            <Download className="h-3 w-3" /> All images
                        </a>
                    )}
                    {hasMedia && (
                        <a
                            href={`${API}/kit/${orderId}/export.csv`}
                            download
                            data-testid="top-csv-btn"
                            title="Upload to Publer.com → auto-publishes 30 days to your IG ($9.99/mo). Includes images, videos, captions, hashtags."
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream font-medium whitespace-nowrap transition-colors"
                        >
                            <Zap className="h-3 w-3" /> Auto-publish CSV
                        </a>
                    )}
                    {calendarLocked ? (
                        <span
                            data-testid="top-ics-locked"
                            title="Confirm Week 1 pre-launch shoot first — see Calendar reminders setup card below."
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-cafe-mocha/40 bg-cafe-cream text-cafe-mocha/70 font-medium whitespace-nowrap cursor-not-allowed"
                        >
                            🔒 Calendar reminders
                        </span>
                    ) : (
                        <a
                            href={`${API}/kit/${orderId}/export.ics`}
                            download
                            data-testid="top-ics-btn"
                            title="Add 30 reminders to your phone calendar. Each rings 30 min before post time with caption ready to copy. Free."
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-cafe-terracotta/50 bg-cafe-cream hover:bg-cafe-terracotta/10 text-cafe-terracotta font-medium whitespace-nowrap transition-colors"
                        >
                            <CalIcon className="h-3 w-3" /> Calendar reminders
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------- Auto-publish CSV setup explainer (Pro/Premium only) ----------
function PublerSetupCard({ orderId }) {
    return (
        <div
            className="bg-cafe-cream border-2 border-cafe-olive/40 rounded-3xl p-6 sm:p-8 mb-12"
            data-testid="publer-setup-card"
        >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-cafe-olive font-bold mb-1.5">
                        Recommended · auto-publish · USD $9.99/month
                    </p>
                    <h3 className="font-serif text-2xl sm:text-3xl text-cafe-espresso leading-tight">
                        Let Publer post all 30 days for you — hands-free.
                    </h3>
                </div>
            </div>

            <p className="text-cafe-mocha text-sm sm:text-base leading-relaxed mb-5">
                Publer is an official Meta partner. You sign up directly with them, connect your Instagram,
                then upload the CSV below. They post all 30 days automatically on your scheduled times — you
                don't touch IG again for a month.
            </p>

            {/* 3-step flow — simplified from the previous 4 steps */}
            <ol className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-5">
                <li className="bg-cafe-latte/40 rounded-2xl px-4 py-3.5 flex gap-3 items-start">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-cafe-olive text-cafe-cream font-display text-sm flex items-center justify-center">
                        1
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-cafe-espresso font-semibold text-[13px] sm:text-sm leading-tight mb-0.5">
                            Sign up at{" "}
                            <a
                                href="https://publer.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cafe-olive underline"
                                data-testid="publer-signup-link"
                            >
                                publer.com
                            </a>
                        </p>
                        <p className="text-cafe-mocha text-xs leading-snug">
                            Pick the Business plan ($9.99/mo, free trial). Connect your Instagram Business or Creator account.
                        </p>
                    </div>
                </li>
                <li className="bg-cafe-latte/40 rounded-2xl px-4 py-3.5 flex gap-3 items-start">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-cafe-olive text-cafe-cream font-display text-sm flex items-center justify-center">
                        2
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-cafe-espresso font-semibold text-[13px] sm:text-sm leading-tight mb-0.5">
                            Download the CSV (button below)
                        </p>
                        <p className="text-cafe-mocha text-xs leading-snug">
                            One file. All 30 posts, captions, hashtags, and your AI-rendered images + reels already inside.
                        </p>
                    </div>
                </li>
                <li className="bg-cafe-latte/40 rounded-2xl px-4 py-3.5 flex gap-3 items-start">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-cafe-olive text-cafe-cream font-display text-sm flex items-center justify-center">
                        3
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-cafe-espresso font-semibold text-[13px] sm:text-sm leading-tight mb-0.5">
                            In Publer: Bulk Schedule → Upload CSV
                        </p>
                        <p className="text-cafe-mocha text-xs leading-snug">
                            Review the 30-post preview Publer shows you → click Schedule. Done — they post for you.
                        </p>
                    </div>
                </li>
            </ol>

            <a
                href={`${API}/kit/${orderId}/export.csv`}
                download
                data-testid="setup-csv-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream text-sm font-semibold uppercase tracking-[0.12em] transition-colors mb-4"
            >
                <Zap className="h-4 w-4" /> Download Publer CSV
            </a>

            <div className="bg-cafe-olive/8 border border-cafe-olive/30 rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-bold mb-1">
                    ✓ With Publer, you don't need the calendar reminders below.
                </p>
                <p className="text-cafe-espresso text-[13px] leading-relaxed">
                    Calendar reminders are only useful if you'd rather post manually (no monthly fee).
                    If you're using Publer, you can skip that section entirely.
                </p>
            </div>
        </div>
    );
}

// ---------- Calendar reminders setup explainer (tier-aware) ----------
function CalendarSetupCard({ orderId, hasImages, week1Shot, onConfirmWeek1Shot, isStarter }) {
    // Starter WITHOUT AI images must shoot Week 1 first — otherwise the reminders fire on Day 1
    // with no photo to attach. New Starter kits include the first 7 days of AI images, so when
    // images exist there is nothing to shoot before Day 1 and the lock is skipped entirely.
    const locked = isStarter && !hasImages && !week1Shot;
    return (
        <div
            className="bg-cafe-cream border-2 border-cafe-terracotta/30 rounded-3xl p-6 sm:p-8 mb-12"
            data-testid="calendar-setup-card"
        >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-cafe-terracotta font-bold mb-1.5">
                        {hasImages
                            ? "Alternative · skip this if you're using Publer above"
                            : "Setup once · 1 minute · free"}
                    </p>
                    <h3 className="font-serif text-2xl sm:text-3xl text-cafe-espresso leading-tight">
                        Or — post manually with a phone reminder.
                    </h3>
                </div>
                {locked ? (
                    <span
                        data-testid="setup-ics-locked"
                        title="Complete the Week 1 pre-launch shoot first — otherwise reminders will fire before you have photos to post."
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-cafe-mocha/30 text-cafe-mocha/90 text-xs font-semibold uppercase tracking-[0.12em] whitespace-nowrap shrink-0 cursor-not-allowed"
                    >
                        🔒 Locked · finish Week 1 first
                    </span>
                ) : (
                    <a
                        href={`${API}/kit/${orderId}/export.ics`}
                        download
                        data-testid="setup-ics-btn"
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream text-xs font-semibold uppercase tracking-[0.12em] whitespace-nowrap transition-colors shrink-0"
                    >
                        <CalIcon className="h-3.5 w-3.5" /> Download · 30 reminders
                    </a>
                )}
            </div>

            <p className="text-cafe-mocha text-sm sm:text-base leading-relaxed mb-5">
                One file. 30 events. Each one rings 30 minutes before post time with the <strong>full caption already inside</strong> — copy from the notification, paste into IG, done.{" "}
                {hasImages
                    ? "Use this only if you'd rather not pay Publer's monthly fee — Publer handles everything automatically."
                    : "This is how you make sure you never forget to post."}
            </p>

            {/* Starter-only gating notice + confirm checkbox — only when Week 1 images
                are NOT included (legacy Starter orders). */}
            {isStarter && !hasImages && (
                <div className={`rounded-2xl border px-4 py-3.5 mb-5 ${locked ? "bg-cafe-terracotta/8 border-cafe-terracotta/40" : "bg-cafe-olive/8 border-cafe-olive/40"}`}>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5" style={{ color: locked ? "#C18B7A" : "#556B2F" }}>
                        {locked ? "⚠️ Why this is locked" : "✓ Pre-launch shoot confirmed"}
                    </p>
                    <p className="text-cafe-espresso text-[13px] sm:text-sm leading-relaxed mb-3">
                        Day 1 only works if you already shot the Week 1 photos. If reminders fire before that, your phone rings but you've got nothing to post. Finish <strong>Saturday Shoot List · Week 1</strong> below first, then come back here.
                    </p>
                    <label className="inline-flex items-start gap-2.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={!!week1Shot}
                            onChange={(e) => onConfirmWeek1Shot(e.target.checked)}
                            data-testid="confirm-week1-shot"
                            className="mt-0.5 w-4 h-4 accent-cafe-olive"
                        />
                        <span className="text-cafe-espresso text-[13px] sm:text-sm leading-snug">
                            I've completed the Week 1 pre-launch shoot — <span className="text-cafe-mocha">unlock the reminders</span>
                        </span>
                    </label>
                </div>
            )}

            {/* 4-step flow */}
            <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {[
                    {
                        n: "1",
                        title: "Tap the button",
                        body: "A file (.ics) downloads to your phone.",
                    },
                    {
                        n: "2",
                        title: "Open the file",
                        body: "iPhone: tap the file → \"Add 30 events\". Android: import to Google Calendar.",
                    },
                    {
                        n: "3",
                        title: "30 events appear",
                        body: "Spaced across the next 30 days at the AI-recommended best time for each post.",
                    },
                    {
                        n: "4",
                        title: "Phone rings, you post",
                        body: "30 min before each post → notification shows up with the full caption ready.",
                    },
                ].map((s) => (
                    <li
                        key={s.n}
                        className={`bg-cafe-latte/40 rounded-2xl px-4 py-3.5 flex gap-3 items-start ${locked ? "opacity-60" : ""}`}
                    >
                        <span className="shrink-0 w-7 h-7 rounded-full bg-cafe-terracotta text-cafe-cream font-display text-sm flex items-center justify-center">
                            {s.n}
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="text-cafe-espresso font-semibold text-[13px] sm:text-sm leading-tight mb-0.5">
                                {s.title}
                            </p>
                            <p className="text-cafe-mocha text-xs leading-snug">
                                {s.body}
                            </p>
                        </div>
                    </li>
                ))}
            </ol>

            <p className="text-[11px] text-cafe-mocha/80 italic mt-4">
                Works on iPhone Calendar, Google Calendar, Apple Calendar (Mac), Outlook. Asia/Kuala_Lumpur timezone is baked in.
            </p>
        </div>
    );
}

// ---------- "How to use" — tier-aware ----------
function HowToUse({ hasImages, hasVideos, onStartPreLaunch, isStarter }) {
    // Pro / Premium customers paid to skip shooting — even if their AI images
    // are still rendering, they should NEVER see the Starter "pre-launch shoot
    // day" guidance. We show the simple 1-step "post when images arrive" path.
    if (!isStarter) {
        // Pro / Premium — images ready, customer only posts
        return (
            <div className="bg-cafe-latte/60 border border-border rounded-3xl p-7 sm:p-9 mb-12" data-testid="how-to-use">
                <p className="text-[10px] uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-2">How to use this kit</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-3">You only do 1 thing now.</h2>
                <p className="text-cafe-mocha text-sm sm:text-base">
                    No shooting. No editing. Open today's card → tap <strong>Save image</strong> + <strong>Copy caption</strong> → paste into IG.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                    <div className="bg-cafe-cream rounded-2xl px-4 py-3.5">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1">Every morning · 30 sec</p>
                        <p className="text-cafe-mocha text-sm leading-snug">
                            Open today's card. Save image + copy caption. Open IG. Paste. Post.
                        </p>
                    </div>
                    {hasVideos && (
                        <div className="bg-cafe-cream rounded-2xl px-4 py-3.5">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-terracotta font-semibold mb-1">Reels · ready-made</p>
                            <p className="text-cafe-mocha text-sm leading-snug">
                                Pre-rendered 8s vertical reels. Save video → upload to IG Reels with the matching caption.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    // Starter — first 7 days of AI images included; customer shoots from Week 2
    if (hasImages) {
        return (
            <div className="bg-cafe-latte/60 border border-border rounded-3xl p-7 sm:p-9 mb-12" data-testid="how-to-use">
                <p className="text-[10px] uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-2">How to use this kit</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-3">Start posting today. Shoot from Week 2.</h2>
                <p className="text-cafe-mocha text-sm sm:text-base">
                    Your <strong>first 7 days of AI images are included</strong> — no shooting needed to launch. From Week 2, a small Saturday shoot rhythm keeps you one week ahead.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                    <div className="bg-cafe-cream rounded-2xl px-4 py-3.5 border-2 border-cafe-olive/40 relative">
                        <span className="absolute -top-2.5 left-3 bg-cafe-olive text-cafe-cream text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-full">
                            Week 1 · done for you
                        </span>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1 mt-1">✓ Step 1 · Post from Day 1</p>
                        <p className="text-cafe-mocha text-sm leading-snug">
                            Open today's card → save the AI image + copy caption → paste into IG. Done in 30 seconds.
                        </p>
                    </div>
                    <div className="bg-cafe-cream rounded-2xl px-4 py-3.5">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1">☕ Step 2 · 5 min every morning</p>
                        <p className="text-cafe-mocha text-sm leading-snug">
                            Open today's card. Copy caption. Attach the photo. Post.
                        </p>
                    </div>
                    <div className="bg-cafe-cream rounded-2xl px-4 py-3.5">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-mocha font-semibold mb-1">📅 Step 3 · Saturdays from Week 2</p>
                        <p className="text-cafe-mocha text-sm leading-snug">
                            Shoot the <strong>next</strong> week's photos using the daily shot scripts. Always one week ahead.
                        </p>
                    </div>
                </div>
                <p className="text-[11px] text-cafe-mocha/80 mt-4 italic">
                    Want to skip shooting entirely? <a href="/#pricing" className="text-cafe-olive underline font-semibold">Upgrade to Standard</a> — we'll render all 30 days of images for you.
                </p>
            </div>
        );
    }
    // Legacy Starter — no images, customer shoots themselves
    return (
        <div className="bg-cafe-latte/60 border border-border rounded-3xl p-7 sm:p-9 mb-12" data-testid="how-to-use">
            <p className="text-[10px] uppercase tracking-[0.25em] text-cafe-olive font-semibold mb-2">How to use this kit</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-3">3 steps. The first one is critical.</h2>
            <p className="text-cafe-mocha text-sm sm:text-base">
                The writing is done. Your job is <strong>one pre-launch shoot day</strong>, then a small daily post + a weekly shoot rhythm.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                <div className="bg-cafe-cream rounded-2xl px-4 py-3.5 border-2 border-cafe-terracotta/40 relative">
                    <span className="absolute -top-2.5 left-3 bg-cafe-terracotta text-cafe-cream text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-full">
                        Do this first
                    </span>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-terracotta font-semibold mb-1 mt-1">📸 Step 1 · Pre-launch shoot (2 hrs)</p>
                    <p className="text-cafe-mocha text-sm leading-snug mb-3">
                        Before Day 1: shoot the <strong>7 photos for Week 1</strong> in one batch. The kit can only start once you have these in your phone.
                    </p>
                    <button
                        type="button"
                        onClick={onStartPreLaunch}
                        data-testid="start-pre-launch-btn"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors"
                    >
                        Click to start the pre-launch shoot
                        <ArrowDown className="w-3 h-3" />
                    </button>
                </div>
                <div className="bg-cafe-cream rounded-2xl px-4 py-3.5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1">☕ Step 2 · 5 min every morning</p>
                    <p className="text-cafe-mocha text-sm leading-snug">
                        Open today's card. Copy caption. Attach the photo from your latest shoot. Post.
                    </p>
                </div>
                <div className="bg-cafe-cream rounded-2xl px-4 py-3.5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-mocha font-semibold mb-1">📅 Step 3 · 2 hrs every Saturday</p>
                    <p className="text-cafe-mocha text-sm leading-snug">
                        Shoot the <strong>next</strong> week's 7 photos. Always one week ahead. Each card tells you exactly what to shoot.
                    </p>
                </div>
            </div>
            <p className="text-[11px] text-cafe-mocha/80 mt-4 italic">
                Want to skip shooting entirely? <a href="/#pricing" className="text-cafe-olive underline font-semibold">Upgrade to Pro</a> — we'll render your 30 images for you.
            </p>
        </div>
    );
}

// ---------- Progress bar ----------
function ProgressBar({ posted, total, onJumpToToday }) {
    const pct = total > 0 ? Math.round((posted / total) * 100) : 0;
    const past50 = pct >= 50;
    const past80 = pct >= 80;
    return (
        <div className="bg-cafe-cream border border-border rounded-2xl px-5 py-4 mb-12 print:hidden" data-testid="progress-bar">
            <div className="flex items-center justify-between text-sm mb-2 gap-3">
                <p className="text-cafe-espresso font-medium">
                    Day <span className="font-serif text-lg">{Math.min(posted + 1, total)}</span> of {total}
                    {past80 && <span className="ml-2 text-cafe-terracotta">🔥 home stretch</span>}
                    {past50 && !past80 && <span className="ml-2 text-cafe-olive">half way there</span>}
                </p>
                <div className="flex items-center gap-3">
                    <p className="text-cafe-mocha text-xs whitespace-nowrap">
                        <span className={past50 ? "text-cafe-terracotta font-semibold" : "text-cafe-olive font-semibold"}>{posted}</span> posted · {total - posted} to go
                    </p>
                    <button
                        onClick={onJumpToToday}
                        data-testid="jump-to-today-btn"
                        className="text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full bg-cafe-espresso hover:bg-cafe-espresso/90 text-cafe-cream transition-colors whitespace-nowrap"
                    >
                        Jump to today ↓
                    </button>
                </div>
            </div>
            <div className="h-1.5 bg-cafe-latte rounded-full overflow-hidden">
                <div
                    className={`h-full transition-all duration-700 ${past50 ? "bg-cafe-terracotta" : "bg-cafe-olive"}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

// ---------- IG Profile mockup with 9-grid ----------
function IgProfileMockup({ cafe, igHandle, location, cafeType, images, calendar }) {
    // Use only the cover slide (slide=1 or no slide field — legacy) for the grid.
    const ready = (images || [])
        .filter(i => i?.status === "ready" && i?.url && (i.slide === undefined || i.slide === 1))
        .slice(0, 9);
    if (!ready.length) return null;
    const stats = fakeStats(cafe);
    const initial = (cafe || "C").charAt(0).toUpperCase();
    const themeFor = (day) => (calendar || []).find(c => c.day === day)?.theme;
    return (
        <section className="mb-16" data-testid="ig-profile-mockup">
            <div className="text-center mb-8">
                <p className="text-[10px] uppercase tracking-[0.3em] text-cafe-olive font-semibold mb-3">Section 02 · the grid plan</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-2">Your IG, all 30 posts at once.</h2>
                <p className="text-cafe-mocha text-sm max-w-xl mx-auto">
                    Plotted as a 9-grid so your profile looks like a curated gallery — not a pile of random posts.
                </p>
            </div>

            <div className="bg-cafe-cream rounded-3xl border border-border p-5 sm:p-8 max-w-3xl mx-auto">
                {/* Profile header */}
                <div className="flex items-start gap-5 sm:gap-7 mb-6">
                    <div
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-cafe-olive text-cafe-cream font-display text-2xl sm:text-3xl flex items-center justify-center shrink-0"
                        aria-hidden
                    >
                        {initial}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-3 mb-1">
                            <h3 className="font-medium text-cafe-espresso text-base sm:text-lg">
                                {igHandle ? `@${igHandle}` : cafe.toLowerCase()}
                            </h3>
                        </div>
                        <p className="text-sm text-cafe-espresso font-semibold mb-0.5">{cafe}</p>
                        <p className="text-xs text-cafe-mocha">
                            {[cafeType, location].filter(Boolean).join(" · ") || "F&B · slow living"}
                        </p>
                        <div className="hidden sm:flex gap-7 mt-3 text-xs">
                            <span><strong className="text-cafe-espresso">{stats.posts}</strong> <span className="text-cafe-mocha">posts</span></span>
                            <span><strong className="text-cafe-espresso">{stats.followers}</strong> <span className="text-cafe-mocha">followers</span></span>
                            <span><strong className="text-cafe-espresso">{stats.following}</strong> <span className="text-cafe-mocha">following</span></span>
                        </div>
                    </div>
                </div>
                <div className="flex sm:hidden gap-5 mb-5 text-xs justify-around bg-cafe-latte/50 rounded-xl py-2.5">
                    <span className="text-center"><strong className="text-cafe-espresso block">{stats.posts}</strong> <span className="text-cafe-mocha">posts</span></span>
                    <span className="text-center"><strong className="text-cafe-espresso block">{stats.followers}</strong> <span className="text-cafe-mocha">followers</span></span>
                    <span className="text-center"><strong className="text-cafe-espresso block">{stats.following}</strong> <span className="text-cafe-mocha">following</span></span>
                </div>

                {/* Critic callout */}
                <div className="mb-5 px-4 py-3 rounded-xl border-l-4 border-cafe-terracotta bg-cafe-terracotta/5">
                    <p className="text-xs sm:text-sm text-cafe-mocha leading-relaxed">
                        <strong className="text-cafe-terracotta">10-second rule.</strong> Visitors decide if they follow you in the first 10 seconds — by looking at your top 9. Below is exactly what those 9 will look like.
                    </p>
                </div>

                {/* 9-grid */}
                <div className="grid grid-cols-3 gap-1 sm:gap-1.5" data-testid="ig-grid">
                    {ready.map((img) => {
                        const theme = img.theme || themeFor(img.day);
                        return (
                            <figure
                                key={img.day}
                                className="relative aspect-square overflow-hidden bg-cafe-latte group"
                                data-testid={`ig-grid-${img.day}`}
                            >
                                <img src={absUrl(img.url)} alt={theme || `Day ${img.day}`} loading="lazy" className="w-full h-full object-cover" />
                                <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-cafe-olive/95 text-cafe-cream flex items-center justify-center">
                                    <Check className="w-3 h-3" strokeWidth={3} />
                                </span>
                            </figure>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ---------- Weekly Shoot Batch (Starter only — no AI images) ----------
// Groups 30 days into ~5 batches; each batch = 7 shoots the customer captures on Saturday.
function WeeklyShootBatch({ calendar, week1Shot, onConfirmWeek1Shot, hasImages }) {
    const [openWeek, setOpenWeek] = useState(0); // first week open by default
    if (!calendar?.length) return null;

    // New Starter kits include AI images for the first 7 days — Week 1 needs no
    // shooting, so the shoot plan starts from Week 2.
    const shootCalendar = hasImages ? calendar.slice(7) : calendar;
    if (!shootCalendar.length) return null;

    // Split into chunks of 7 days
    const weeks = [];
    for (let i = 0; i < shootCalendar.length; i += 7) {
        weeks.push(shootCalendar.slice(i, i + 7));
    }

    return (
        <section className="mb-16" data-testid="weekly-shoot-batch">
            <div className="text-center mb-8">
                <p className="text-[10px] uppercase tracking-[0.3em] text-cafe-terracotta font-semibold mb-3">Section 02 · your shooting plan</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-3">Saturday shoot lists.</h2>
                <p className="text-cafe-mocha text-sm sm:text-base max-w-2xl mx-auto">
                    {hasImages ? (
                        <><strong>Week 1 is covered — your first 7 days of AI images are included.</strong> Every Saturday, shoot the next week's list to stay one week ahead of your posts.</>
                    ) : (
                        <><strong>Week 1 must be shot BEFORE Day 1.</strong> Every Saturday after that, shoot the next 7 to stay one week ahead of your posts.</>
                    )}
                </p>
            </div>

            <div className="space-y-3">
                {weeks.map((week, wi) => {
                    const isOpen = openWeek === wi;
                    const firstDay = week[0]?.day;
                    const lastDay = week[week.length - 1]?.day;
                    const reelCount = week.filter(d => /reel/i.test(d.format || "")).length;
                    const photoCount = week.length - reelCount;
                    const isPreLaunch = wi === 0 && !hasImages;
                    return (
                        <div
                            key={wi}
                            className={`rounded-2xl overflow-hidden ${isPreLaunch ? "border-2 border-cafe-terracotta bg-cafe-terracotta/5" : "border border-cafe-terracotta/30 bg-cafe-cream"}`}
                            data-testid={`shoot-week-${wi + 1}`}
                        >
                            <button
                                onClick={() => setOpenWeek(isOpen ? -1 : wi)}
                                className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 text-left hover:bg-cafe-terracotta/10 transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    {isPreLaunch ? (
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-terracotta font-bold mb-0.5 flex flex-wrap items-center gap-2">
                                            <span className="bg-cafe-terracotta text-cafe-cream px-2 py-0.5 rounded-full text-[9px]">⚠ Pre-launch</span>
                                            Shoot BEFORE Day 1 · sets up your first week
                                        </p>
                                    ) : (
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-terracotta font-bold mb-0.5">
                                            Saturday of Week {hasImages ? wi + 1 : wi} · for Days {firstDay}–{lastDay}
                                        </p>
                                    )}
                                    <p className="text-cafe-espresso font-medium text-sm sm:text-base">
                                        Day {firstDay}–{lastDay} · <span className="text-cafe-mocha font-normal">{photoCount} photo{photoCount !== 1 ? "s" : ""}{reelCount ? ` + ${reelCount} reel${reelCount > 1 ? "s" : ""}` : ""}</span>
                                    </p>
                                </div>
                                {isOpen ? <ChevronDown className="w-5 h-5 text-cafe-terracotta shrink-0" /> : <ChevronRight className="w-5 h-5 text-cafe-terracotta shrink-0" />}
                            </button>
                            {isOpen && (
                                <div className="border-t border-cafe-terracotta/20 px-5 sm:px-6 py-5 bg-cafe-latte/30 space-y-4">
                                    {week.map((d, i) => (
                                        <div key={d.day} className="flex gap-4 items-start pb-4 border-b border-border/40 last:border-b-0 last:pb-0">
                                            <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-cafe-terracotta/10 border border-cafe-terracotta/30 text-cafe-terracotta font-serif text-sm sm:text-base flex items-center justify-center">
                                                {i + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[10px] uppercase tracking-[0.15em] text-cafe-mocha font-semibold mb-0.5">
                                                    Day {d.day} · {d.format || "Post"}{d.best_time ? ` · ${d.best_time}` : ""}
                                                </p>
                                                <p className="font-serif text-base sm:text-lg text-cafe-espresso leading-tight mb-1.5">
                                                    {d.theme || `Day ${d.day}`}
                                                </p>
                                                {d.shoot_note ? (
                                                    <p className="text-[13px] sm:text-sm text-cafe-mocha leading-relaxed">
                                                        📸 {d.shoot_note}
                                                    </p>
                                                ) : (
                                                    <p className="text-[12px] text-cafe-mocha/70 italic">
                                                        Shoot an on-brand photo for this theme.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Pre-launch confirm checkbox at end of Week 1 */}
                                    {isPreLaunch && (
                                        <div className="mt-4 pt-4 border-t-2 border-cafe-terracotta/30">
                                            <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl bg-cafe-cream hover:bg-cafe-cream/80 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={!!week1Shot}
                                                    onChange={(e) => onConfirmWeek1Shot(e.target.checked)}
                                                    data-testid="confirm-week1-shot-batch"
                                                    className="mt-0.5 w-5 h-5 accent-cafe-olive shrink-0"
                                                />
                                                <span className="flex-1 text-cafe-espresso text-sm sm:text-base leading-snug">
                                                    <strong>I've shot all 7 above</strong> — I'm ready to start Day 1 and unlock the calendar reminders.
                                                </span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="text-center mt-6 text-[11px] text-cafe-mocha/80 italic">
                Pro tip · shoot everything in one 2-hour block on Saturday morning. Morning light = most flexible for post-processing.
            </p>
        </section>
    );
}

// ---------- Reference Library (collapsible) ----------
function ReferenceLibrary({ draft, tier }) {
    const [open, setOpen] = useState(null);
    // Pro/Premium customers paid to skip the shoot entirely. The "Reel scripts"
    // (extra reels they'd shoot themselves) and the Visual guide's shot-list
    // + phone-camera settings are Starter-only deliverables — surfacing them
    // to Pro/Premium creates confusion about whether more shooting is needed.
    const isPaidMedia = tier === "pro" || tier === "premium";
    const sections = [
        {
            key: "brand_voice",
            label: "Brand voice & rules",
            sub: "Use this when writing any caption, replying to comments, or training new staff. Keeps everyone on-brand.",
            body: draft.brand_voice,
        },
        {
            key: "captions",
            label: "Caption templates (by category)",
            sub: "Reuse these for month 2, 3, and beyond — or for unplanned moments (launches, awards, viral comments).",
            body: draft.captions,
        },
        {
            key: "hooks",
            label: "Hooks library (by angle)",
            sub: "First-line magnets. Swap into any custom Reel or carousel when engagement is low.",
            body: draft.hooks,
        },
        // Reel scripts (extra ready-to-shoot reels) — Starter-only, and only when
        // the draft actually contains them (new Standard/Starter kits ship none).
        !isPaidMedia && (draft.reels || []).length > 0 && {
            key: "reels",
            label: "Reel scripts",
            sub: "Extra ready-to-shoot Reels beyond your 30-day calendar — open when you need bonus content.",
            body: draft.reels,
        },
        {
            key: "visual_guide",
            label: "Visual guide",
            sub: isPaidMedia
                ? "Your brand palette & mood reference — for moments when you want to brief a designer or shoot fresh content yourself."
                : "Your shoot bible. Palette + 12 specific shots + phone camera settings — refer to this every time you pick up your phone.",
            body: draft.visual_guide,
        },
    ].filter(Boolean);

    return (
        <section className="mb-16" data-testid="reference-library">
            <div className="flex items-center gap-3 mb-3">
                <BookOpen className="w-5 h-5 text-cafe-mocha" />
                <h2 className="font-serif text-2xl sm:text-3xl text-cafe-espresso">Reference library</h2>
            </div>
            <div className="bg-cafe-latte/40 border-l-4 border-cafe-olive rounded-r-2xl px-5 py-4 mb-6">
                <p className="text-[10px] uppercase tracking-[0.25em] text-cafe-olive font-bold mb-1.5">
                    Your brand operating manual
                </p>
                <p className="text-cafe-espresso text-sm sm:text-base leading-relaxed">
                    The 30 cards above tell you exactly what to do for the next 30 days. <strong>This section is what keeps you on-brand for the next 12 months</strong> — caption templates, hooks, scripts, voice rules, and a full shoot bible. Open it now to skim, then come back whenever you write something new.
                </p>
            </div>
            <div className="space-y-2">
                {sections.map(s => {
                    if (!s.body) return null;
                    const isOpen = open === s.key;
                    return (
                        <div key={s.key} className="border border-border rounded-2xl bg-cafe-cream overflow-hidden">
                            <button
                                onClick={() => setOpen(isOpen ? null : s.key)}
                                data-testid={`ref-${s.key}-toggle`}
                                className="w-full flex items-start justify-between gap-3 px-5 py-4 text-left hover:bg-cafe-latte/30 transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-cafe-espresso text-sm sm:text-base">{s.label}</p>
                                    {s.sub && (
                                        <p className="text-xs text-cafe-mocha/85 leading-snug mt-1">{s.sub}</p>
                                    )}
                                </div>
                                {isOpen ? <ChevronDown className="w-4 h-4 text-cafe-mocha shrink-0 mt-1" /> : <ChevronRight className="w-4 h-4 text-cafe-mocha shrink-0 mt-1" />}
                            </button>
                            {isOpen && (
                                <div className="px-5 pb-5 text-sm text-cafe-mocha border-t border-border/60">
                                    <RefBody section={s.key} body={s.body} tier={tier} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

// ---------- Small helper: copyable text line with an inline button ----------
function CopyableLine({ text, testId }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success("Copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed — long-press to select & copy");
        }
    };
    return (
        <div className="group flex items-start justify-between gap-3 bg-cafe-latte/40 hover:bg-cafe-latte/70 rounded-lg px-3 py-2 transition-colors">
            <p className="text-sm text-cafe-espresso leading-relaxed whitespace-pre-line flex-1">{text}</p>
            <button
                onClick={copy}
                data-testid={testId}
                title="Copy"
                className="shrink-0 opacity-60 hover:opacity-100 text-cafe-olive transition-opacity"
            >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
        </div>
    );
}

// ---------- Renders a dict-of-string-arrays as a tabbed/category list ----------
function CategoryList({ body, accent = "olive" }) {
    const entries = Object.entries(body || {}).filter(([, v]) => Array.isArray(v) && v.length > 0);
    if (!entries.length) return <p className="text-xs text-cafe-mocha/70 italic pt-3">No items yet — the writing team will refine this.</p>;
    return (
        <div className="space-y-5 pt-4">
            {entries.map(([cat, items]) => (
                <div key={cat}>
                    <p className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-2 ${accent === "terracotta" ? "text-cafe-terracotta" : "text-cafe-olive"}`}>
                        {cat.replace(/_/g, " ")} · {items.length}
                    </p>
                    <div className="space-y-1.5">
                        {items.map((line, i) => (
                            <CopyableLine key={i} text={typeof line === "string" ? line : JSON.stringify(line)} testId={`ref-${cat}-${i}`} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function RefBody({ section, body, tier }) {
    if (section === "brand_voice") {
        return (
            <div className="space-y-4 pt-4">
                {body.summary && (
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1.5">Summary</p>
                        <p className="text-cafe-espresso leading-relaxed whitespace-pre-line">{body.summary}</p>
                    </div>
                )}
                {body.essence && (
                    <p><strong className="text-cafe-espresso">Essence: </strong>{body.essence}</p>
                )}
                {body.pillars?.length > 0 && (
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1.5">Pillars</p>
                        <ul className="list-disc pl-5 space-y-1 text-cafe-espresso">{body.pillars.map((p, i) => <li key={i}>{p}</li>)}</ul>
                    </div>
                )}
                {(body.do_say?.length > 0 || body.dont_say?.length > 0) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {body.do_say?.length > 0 && (
                            <div className="bg-cafe-olive/5 rounded-xl p-3.5 border border-cafe-olive/20">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-bold mb-2">Do say</p>
                                <ul className="space-y-1.5 text-sm text-cafe-espresso">{body.do_say.map((s, i) => <li key={i} className="leading-snug">· {s}</li>)}</ul>
                            </div>
                        )}
                        {body.dont_say?.length > 0 && (
                            <div className="bg-cafe-terracotta/5 rounded-xl p-3.5 border border-cafe-terracotta/20">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-terracotta font-bold mb-2">Don't say</p>
                                <ul className="space-y-1.5 text-sm text-cafe-espresso">{body.dont_say.map((s, i) => <li key={i} className="leading-snug">· {s}</li>)}</ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (section === "captions") {
        // Claude returns dict of categories (story / behind / community / calendar / educate / sell) each with list of full caption strings.
        // Legacy shape: body.templates = [{name, template|body}]
        if (Array.isArray(body.templates)) {
            return (
                <div className="space-y-3 pt-4">
                    {body.templates.map((t, i) => (
                        <div key={i} className="bg-cafe-latte/40 rounded-xl p-3.5">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1">{t.name || `Template ${i + 1}`}</p>
                            <p className="whitespace-pre-line text-cafe-espresso text-sm">{t.template || t.body}</p>
                        </div>
                    ))}
                </div>
            );
        }
        return <CategoryList body={body} accent="olive" />;
    }

    if (section === "hooks") {
        // Claude returns dict of angles (curiosity / controversial / list / story / question) each with a list of short hooks.
        // Legacy shape: body.list = [string, ...]
        if (Array.isArray(body.list)) {
            return (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-4">
                    {body.list.map((h, i) => (
                        <li key={i} className="text-sm text-cafe-espresso bg-cafe-latte/40 rounded-lg px-3 py-2">{h}</li>
                    ))}
                </ul>
            );
        }
        return <CategoryList body={body} accent="terracotta" />;
    }

    if (section === "reels") {
        // Claude may return array of {title, hook, script|steps, cta} OR dict categorized.
        const list = Array.isArray(body) ? body : (body.scripts || body.list || []);
        if (Array.isArray(list) && list.length > 0) {
            return (
                <div className="space-y-4 pt-4">
                    {list.map((r, i) => (
                        <div key={i} className="bg-cafe-latte/40 rounded-xl p-4 border border-border/60">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-terracotta font-bold mb-1">Reel {i + 1}{r.duration ? ` · ${r.duration}` : ""}</p>
                            {r.title && <p className="font-serif text-base text-cafe-espresso mb-1">{r.title}</p>}
                            {r.hook && <p className="font-display italic text-cafe-olive text-sm mb-2">"{r.hook}"</p>}
                            {typeof r.script === "string" && (
                                <p className="whitespace-pre-line text-cafe-espresso text-sm leading-relaxed">{r.script}</p>
                            )}
                            {Array.isArray(r.script) && (
                                <ol className="list-decimal pl-5 space-y-1 text-sm text-cafe-espresso">
                                    {r.script.map((s, j) => <li key={j}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}
                                </ol>
                            )}
                            {Array.isArray(r.steps) && (
                                <ol className="list-decimal pl-5 space-y-1 text-sm text-cafe-espresso">
                                    {r.steps.map((s, j) => <li key={j}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}
                                </ol>
                            )}
                            {r.cta && <p className="text-[11px] text-cafe-olive mt-2"><strong className="uppercase tracking-[0.15em]">CTA:</strong> {r.cta}</p>}
                        </div>
                    ))}
                </div>
            );
        }
        // Fallback for dict shape
        return <CategoryList body={body} accent="terracotta" />;
    }

    if (section === "visual_guide") {
        const palette = Array.isArray(body.palette) ? body.palette : [];
        // Pro/Premium customers paid to skip the shoot — surfacing a 12-shot
        // checklist + phone camera settings to them is misleading (suggests
        // they still need to shoot). Mood + palette + filter recipe stay
        // useful as a brand reference; the rest is Starter-only.
        const isPaidMedia = tier === "pro" || tier === "premium";
        return (
            <div className="space-y-4 pt-4">
                {body.mood && (
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1.5">Mood</p>
                        <p className="text-cafe-espresso leading-relaxed whitespace-pre-line">{body.mood}</p>
                    </div>
                )}
                {palette.length > 0 && (
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-2">Palette</p>
                        <div className="flex flex-wrap gap-3">
                            {palette.map((c, i) => {
                                const hex = typeof c === "string" ? c : (c.hex || c.color || "");
                                const name = typeof c === "object" ? (c.name || c.label || "") : "";
                                return (
                                    <div key={i} className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-md border border-border" style={{ background: hex }} />
                                        <span className="text-xs font-mono text-cafe-mocha">{hex}{name ? ` · ${name}` : ""}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {!isPaidMedia && Array.isArray(body.shot_list) && body.shot_list.length > 0 && (
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-2">Shot list</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-cafe-espresso">
                            {body.shot_list.map((s, i) => <li key={i}>{typeof s === "string" ? s : (s.shot || s.description || JSON.stringify(s))}</li>)}
                        </ul>
                    </div>
                )}
                {!isPaidMedia && body.phone_settings && (
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-2">Phone camera settings</p>
                        {typeof body.phone_settings === "string" ? (
                            <p className="text-sm text-cafe-espresso leading-relaxed whitespace-pre-line">{body.phone_settings}</p>
                        ) : (
                            <ul className="space-y-1 text-sm text-cafe-espresso">
                                {Object.entries(body.phone_settings).map(([k, v]) => (
                                    <li key={k}><strong className="uppercase tracking-[0.12em] text-[11px] text-cafe-mocha mr-2">{k.replace(/_/g, " ")}:</strong>{typeof v === "string" ? v : JSON.stringify(v)}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {Array.isArray(body.reference_takeaways) && body.reference_takeaways.length > 0 && (
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-2">Reference takeaways</p>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-cafe-espresso">
                            {body.reference_takeaways.map((s, i) => <li key={i}>{typeof s === "string" ? s : JSON.stringify(s)}</li>)}
                        </ul>
                    </div>
                )}
                {body.filter_recipe && (
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-1">Filter recipe</p>
                        <pre className="bg-cafe-latte/60 rounded-lg p-3 text-xs whitespace-pre-wrap font-mono text-cafe-espresso">
                            {typeof body.filter_recipe === "string" ? body.filter_recipe : JSON.stringify(body.filter_recipe, null, 2)}
                        </pre>
                    </div>
                )}
                {Array.isArray(body.tips) && body.tips.length > 0 && (
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                        {body.tips.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                )}
            </div>
        );
    }
    return <pre className="text-xs whitespace-pre-wrap pt-3">{JSON.stringify(body, null, 2)}</pre>;
}

// ---------- Main Kit page ----------
/** Slim member-area top bar shown when the Kit page is embedded inside the
 *  member hub at /member/kit/:id — replaces the public SiteNav. */
function MemberKitBar() {
    return (
        <header className="sticky top-0 z-40 bg-cafe-cream/90 backdrop-blur border-b border-border" data-testid="member-kit-bar">
            <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
                <a href="/member" className="font-serif text-xl text-cafe-espresso hover:text-cafe-olive transition-colors" data-testid="member-kit-logo">
                    kaily<span className="text-cafe-terracotta">.</span>
                </a>
                <a
                    href="/member"
                    className="inline-flex items-center gap-1.5 bg-white border border-border hover:border-cafe-olive text-cafe-espresso rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] transition-colors"
                    data-testid="member-kit-back-btn"
                >
                    ← Member Hub
                </a>
            </div>
        </header>
    );
}

export default function Kit({ memberMode = false }) {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // Persisted "posted" tracking per device
    const storageKey = useMemo(() => `kit-${id}-posted`, [id]);
    const [posted, setPosted] = useState(() => {
        try {
            const raw = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
            return new Set(raw ? JSON.parse(raw) : []);
        } catch {
            return new Set();
        }
    });

    // Persisted "Week 1 pre-launch shoot completed" flag — gates calendar reminders for Starter tier
    const week1StorageKey = useMemo(() => `kit-${id}-week1-shot`, [id]);
    const [week1Shot, setWeek1Shot] = useState(() => {
        try {
            return typeof window !== "undefined" && window.localStorage.getItem(week1StorageKey) === "true";
        } catch {
            return false;
        }
    });
    const onConfirmWeek1Shot = (checked) => {
        setWeek1Shot(checked);
        try { window.localStorage.setItem(week1StorageKey, checked ? "true" : "false"); } catch { /* noop */ }
        if (checked) {
            toast.success("Pre-launch shoot confirmed", {
                description: "Calendar reminders unlocked. Scrolling up to download…",
                duration: 5000,
            });
            // Auto-scroll to CalendarSetupCard so customer can immediately download
            setTimeout(() => {
                const el = document.querySelector('[data-testid="calendar-setup-card"]');
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    // Brief pulse highlight to draw the eye
                    el.classList.add("ring-4", "ring-cafe-terracotta/60", "ring-offset-2");
                    setTimeout(() => el.classList.remove("ring-4", "ring-cafe-terracotta/60", "ring-offset-2"), 2500);
                }
            }, 300);
        }
    };

    // Step 1 → scroll to Saturday Shoot List Week 1 (and auto-open it if collapsed)
    const onStartPreLaunch = () => {
        const target = document.querySelector('[data-testid="shoot-week-1"]');
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
            target.classList.add("ring-4", "ring-cafe-terracotta/60", "ring-offset-2");
            setTimeout(() => target.classList.remove("ring-4", "ring-cafe-terracotta/60", "ring-offset-2"), 2500);
        }
    };

    // Refs to each day card for jump-to-today
    const dayRefs = useRef({});
    const registerRef = (day, el) => {
        if (el) dayRefs.current[day] = el;
    };

    // Track previous posted count for milestone toasts
    const prevPostedCount = useRef(posted.size);

    const togglePosted = (day) => {
        setPosted(prev => {
            const next = new Set(prev);
            const wasPosted = next.has(day);
            if (wasPosted) next.delete(day);
            else next.add(day);
            try { window.localStorage.setItem(storageKey, JSON.stringify([...next])); } catch { /* noop */ }

            // Milestone celebration only when NEWLY posting (not un-posting)
            if (!wasPosted) {
                const newCount = next.size;
                const milestones = {
                    7: { msg: "🎉 Week 1 done!", desc: "23 days to go. You're building a real rhythm." },
                    14: { msg: "🔥 Halfway there!", desc: "Your IG grid is taking shape. Keep going." },
                    21: { msg: "💪 Week 3 conquered!", desc: "Just 9 more days. The hard part is behind you." },
                    30: { msg: "🏆 30/30 — you did it!", desc: "Time to plan the next 30. Reply to your delivery email." },
                };
                if (milestones[newCount]) {
                    setTimeout(() => toast.success(milestones[newCount].msg, {
                        description: milestones[newCount].desc,
                        duration: 7000,
                    }), 100);
                }
                prevPostedCount.current = newCount;
            }
            return next;
        });
    };

    // Find first un-posted day → "today's" target card
    const firstUnpostedDay = useMemo(() => {
        const cal = (data?.ai_draft?.calendar_30d) || [];
        const firstFree = cal.find(c => !posted.has(c.day));
        return firstFree?.day || cal[0]?.day || 1;
    }, [data, posted]);

    const jumpToToday = () => {
        const el = dayRefs.current[firstUnpostedDay];
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            // Brief highlight
            el.classList.add("ring-2", "ring-cafe-terracotta", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-cafe-terracotta", "ring-offset-2"), 2000);
        }
    };

    // Auto-scroll to today's card on first load (after data + refs are ready)
    const autoScrollDoneRef = useRef(false);
    useEffect(() => {
        if (autoScrollDoneRef.current || !data || posted.size === 0) return;
        // If user has posted at least 1 day, jump to next un-posted day on load
        const t = setTimeout(() => {
            const el = dayRefs.current[firstUnpostedDay];
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            autoScrollDoneRef.current = true;
        }, 1200);
        return () => clearTimeout(t);
    }, [data, firstUnpostedDay, posted.size]);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/kit/${id}`)
            .then(res => { if (!cancelled) setData(res.data); })
            .catch(e => { if (!cancelled) setErr(e?.response?.data?.detail || "Kit not found"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [id]);

    // First-visit reminder — dismissable, persisted per device. Tells the
    // customer to BOOKMARK the kit URL and USE "Mark as posted" so they
    // never lose the link or accidentally double-post. Defined here (top of
    // component, before any early return) to comply with React hooks rules.
    const tipDismissKey = `kit-${id}-tip-dismissed`;
    const [showSaveTip, setShowSaveTip] = useState(() => {
        try {
            return typeof window !== "undefined" && window.localStorage.getItem(tipDismissKey) !== "true";
        } catch {
            return true;
        }
    });
    const dismissSaveTip = () => {
        setShowSaveTip(false);
        try { window.localStorage.setItem(tipDismissKey, "true"); } catch { /* noop */ }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-cafe-cream flex items-center justify-center">
                <p className="text-cafe-mocha">Loading your kit…</p>
            </div>
        );
    }
    if (err || !data) {
        return (
            <div className="min-h-screen bg-cafe-cream flex flex-col">
                {memberMode ? <MemberKitBar /> : <SiteNav />}
                <div className="flex-1 flex items-center justify-center px-6 text-center">
                    <div>
                        <h1 className="font-serif text-3xl text-cafe-espresso mb-3">Kit not found</h1>
                        <p className="text-cafe-mocha text-sm">{err || "Check the link and try again."}</p>
                    </div>
                </div>
                {!memberMode && <SiteFooter />}
            </div>
        );
    }

    const draft = data.ai_draft || {};
    const intake = data.intake || {};
    const cafe = intake.cafe_name || data.customer_name || "your cafe";
    const igHandle = cleanIgHandle(intake.ig_handle);
    const location = intake.location || "";
    const cafeType = (intake.cafe_type || []).slice(0, 1).join("");

    const calendar = draft.calendar_30d || [];
    const aiImages = data.ai_images || [];
    const aiVideos = data.ai_videos || [];
    // For each day, gather all ready slides (sorted by slide number) — cover, then supporting carousel slides.
    const slidesByDay = (day) => aiImages
        .filter(i => i.day === day && i.status === "ready" && i.url)
        .sort((a, b) => (a.slide || 1) - (b.slide || 1));
    // Day → matching reel video (Reel-format days are mapped in order to ai_videos[0..N-1]).
    const reelForDay = (() => {
        const reelDays = calendar.filter(c => /reel/i.test(c.format || ""));
        const readyVideos = aiVideos.filter(v => v?.status === "ready" && v?.url);
        const map = {};
        reelDays.forEach((d, idx) => {
            if (readyVideos[idx]) map[d.day] = readyVideos[idx];
        });
        return (day) => map[day] || null;
    })();

    const totalDays = calendar.length || 30;
    const postedCount = posted.size;
    const hasImages = aiImages.some(i => i.status === "ready" && i.url);
    const hasVideos = aiVideos.some(v => v.status === "ready" && v.url);
    // Tier (premium | pro | starter) — derived from the order's product_name
    // server-side. Pro/Premium customers MUST NOT be shown Starter shoot-list
    // content even when their AI images are still rendering — they paid to
    // skip shooting, and seeing DIY guidance would feel like a downgrade.
    const tier = data.tier || "starter";
    const isStarter = tier === "starter";
    const isPaidMedia = tier === "pro" || tier === "premium";
    const pendingImages = isPaidMedia && !hasImages;
    const pendingVideos = tier === "premium" && !hasVideos;
    // Calendar reminders are gated for Starter (no images) until Week 1 pre-launch shoot is confirmed.
    // For Pro/Premium we never lock the calendar — even if AI media is still rendering, the captions
    // are deliverable on day 1, and the customer can use their own photos in the meantime.
    const calendarLocked = isStarter && !hasImages && !week1Shot;

    return (
        <div className="min-h-screen bg-cafe-cream">
            {memberMode ? <MemberKitBar /> : <SiteNav />}
            <TopExportBar orderId={id} hasMedia={hasImages || hasVideos} hasImages={hasImages} calendarLocked={calendarLocked} />

            <main className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-20 print:pt-0 print:px-0">
                {/* System version trust badge + subscriber status — both
                    sit in the top-right strip; the subscriber pill only
                    renders for active subs. */}
                <div className="mb-5 flex justify-end items-center gap-2 print:hidden" data-testid="kit-sysver">
                    <SubscriberBadge email={data.customer_email} />
                    <SystemVersionBadge />
                </div>

                {/* Hero */}
                <IntroBlock cafe={cafe} igHandle={igHandle} />

                {/* First-visit reminder banner */}
                {showSaveTip && (
                    <div
                        data-testid="save-and-track-tip"
                        className="mb-8 sm:mb-10 bg-cafe-cream border-2 border-cafe-terracotta/40 rounded-2xl p-4 sm:p-5 flex items-start gap-3 print:hidden"
                    >
                        <div className="shrink-0 w-9 h-9 rounded-full bg-cafe-terracotta/15 flex items-center justify-center text-cafe-terracotta font-bold">
                            !
                        </div>
                        <div className="flex-1 text-[13px] sm:text-sm text-cafe-espresso leading-relaxed">
                            <p className="font-semibold text-cafe-terracotta uppercase tracking-[0.15em] text-[10px] mb-1.5">2 things before you start</p>
                            <p className="mb-1.5">
                                <strong>1. Bookmark this page</strong> — your kit lives here permanently. Add to home screen / save the URL.
                            </p>
                            <p>
                                <strong>2. After you post each day</strong>, tick <em>"Mark as posted"</em> on the card — the kit then auto-jumps to tomorrow's post so you never lose track.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={dismissSaveTip}
                            data-testid="dismiss-save-tip"
                            className="shrink-0 text-cafe-mocha hover:text-cafe-espresso text-xs font-semibold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border border-cafe-olive/30 hover:bg-cafe-olive/10 transition-colors"
                            aria-label="Got it"
                        >
                            Got it
                        </button>
                    </div>
                )}

                {/* How to use */}
                <HowToUse hasImages={hasImages} hasVideos={hasVideos} onStartPreLaunch={onStartPreLaunch} isStarter={isStarter} />

                {/* Auto-publish CSV setup explainer (Pro/Premium only) */}
                {isPaidMedia && <PublerSetupCard orderId={id} />}

                {/* Pro/Premium: AI media still rendering — show a friendly waiting banner
                    instead of letting the page fall back to Starter shoot-list UX. */}
                <MediaPendingBanner tier={tier} pendingImages={pendingImages} pendingVideos={pendingVideos} />

                {/* Calendar reminders setup explainer */}
                <CalendarSetupCard orderId={id} hasImages={hasImages} week1Shot={week1Shot} onConfirmWeek1Shot={onConfirmWeek1Shot} isStarter={isStarter} />

                {/* Progress */}
                <ProgressBar posted={postedCount} total={totalDays} onJumpToToday={jumpToToday} />

                {/* IG profile mockup (Pro/Premium only — shows only when images are ready) */}
                <IgProfileMockup
                    cafe={cafe}
                    igHandle={igHandle}
                    location={location}
                    cafeType={cafeType}
                    images={aiImages}
                    calendar={calendar}
                />

                {/* Weekly Shoot Batch — Starter tier ONLY (Pro/Premium customers paid to
                    skip shooting; we surface the MediaPendingBanner above instead). */}
                {isStarter && <WeeklyShootBatch calendar={calendar} week1Shot={week1Shot} onConfirmWeek1Shot={onConfirmWeek1Shot} hasImages={hasImages} />}

                {/* Reels */}
                <ReelsStrip videos={aiVideos} />

                {/* 30 daily action cards */}
                <section className="mb-16" data-testid="daily-cards-section">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.3em] text-cafe-olive font-semibold mb-1">Section 03</p>
                            <h2 className="font-serif text-3xl sm:text-4xl text-cafe-espresso">30-Day Daily Cards</h2>
                        </div>
                        <p className="text-cafe-mocha text-xs sm:text-sm">
                            Open today's. Save image. Copy caption. Post.
                        </p>
                    </div>
                    <div className="space-y-4">
                        {calendar.map(entry => (
                            <DayCard
                                key={entry.day}
                                entry={entry}
                                image={slidesByDay(entry.day)[0] || null}
                                slides={slidesByDay(entry.day)}
                                reel={reelForDay(entry.day)}
                                isPosted={posted.has(entry.day)}
                                onTogglePosted={togglePosted}
                                registerRef={registerRef}
                                orderId={id}
                            />
                        ))}
                    </div>
                </section>

                {/* Reference library */}
                <ReferenceLibrary draft={draft} tier={tier} />

                {/* Monthly continuity upsell — surfaces RM 68/168/348 subscription
                    tiers right where the kit "ends", catching customers in the
                    moment they realise the kit ships but IG keeps moving. */}
                <ContinuityCTA orderId={id} email={data.customer_email || ""} cafe={cafe} />

                {/* Reviewed by Kaily — boutique service signature */}
                <ChannelBlock cafe={cafe} />
            </main>

            {!memberMode && <SiteFooter />}
        </div>
    );
}

/** Small pill that lights up at the top of the Kit page if this customer
 *  already has an active Continuity subscription. Mirrors the more verbose
 *  ContinuityCTA at the bottom of the page — but visible above the fold so
 *  the customer feels the membership instantly on page load. */
function SubscriberBadge({ email }) {
    const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const [tier, setTier] = useState(null);
    useEffect(() => {
        if (!email) return;
        axios.post(`${API_BASE}/subscriptions/status`, { email })
            .then((r) => { if (r.data?.has_subscription) setTier(r.data.tier); })
            .catch(() => {});
    }, [email]);

    if (!tier) return null;
    const TIER_LABEL = {
        "continuity-68": "Lite",
        "continuity-168": "Standard",
        "continuity-348": "Pro",
    };
    return (
        <span
            className="inline-flex items-center gap-1.5 bg-cafe-terracotta/10 border border-cafe-terracotta/30 rounded-full px-3 py-1.5"
            data-testid="kit-subscriber-badge"
            title="You're an active Continuity subscriber"
        >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-cafe-terracotta animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.16em] text-cafe-terracotta font-bold">Subscriber · {TIER_LABEL[tier] || tier}</span>
        </span>
    );
}

/** ContinuityCTA — surfaces the 3 monthly subscription tiers (RM 68/168/348)
 *  at the bottom of every delivered kit page. Hidden if the customer is
 *  already an active subscriber (we check via /subscriptions/status).
 *  This is the entry point for our biggest LTV lever — converting one-time
 *  buyers into MRR. Strategically placed AFTER the customer has scrolled
 *  through their entire delivered kit, when they're feeling the value most. */
function ContinuityCTA({ orderId, email, cafe }) {
    const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const [hasSub, setHasSub] = useState(false);
    useEffect(() => {
        if (!email) return;
        axios.post(`${API_BASE}/subscriptions/status`, { email })
            .then((r) => setHasSub(!!r.data?.has_subscription))
            .catch(() => {});
    }, [email]);

    if (hasSub) {
        return (
            <section
                className="my-10 bg-cafe-olive/10 border border-cafe-olive/30 rounded-2xl p-5 sm:p-6 text-center print:hidden"
                data-testid="continuity-active"
            >
                <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-1">Continuity active</p>
                <p className="text-sm text-cafe-mocha">
                    You're subscribed — fresh trends + media drop into your inbox every month. Thank you for keeping {cafe} sharp.
                </p>
            </section>
        );
    }

    const params = new URLSearchParams();
    if (orderId) params.set("from", orderId);
    if (email) params.set("email", email);
    const href = `/subscribe?${params.toString()}`;

    return (
        <section
            className="my-10 bg-cafe-cream border-2 border-cafe-terracotta/30 rounded-2xl p-6 sm:p-8 print:hidden"
            data-testid="continuity-cta"
        >
            <div className="flex flex-col sm:flex-row gap-5 items-center">
                <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-terracotta font-bold mb-2">
                        Day 30 isn't the end
                    </p>
                    <h3 className="font-serif text-2xl text-cafe-espresso leading-tight mb-2">
                        Keep {cafe} sharp every month.
                    </h3>
                    <p className="text-sm text-cafe-mocha leading-relaxed">
                        The IG algorithm shifts every 30 days. Subscribe from <strong>RM 68/mo</strong> for monthly trends + fresh AI media — built on the same system that shipped this kit.
                    </p>
                </div>
                <a
                    href={href}
                    className="inline-flex items-center gap-2 bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream rounded-full px-6 py-3.5 text-xs font-bold uppercase tracking-[0.14em] whitespace-nowrap transition-all hover:-translate-y-0.5"
                    data-testid="continuity-cta-btn"
                >
                    See plans →
                </a>
            </div>
        </section>
    );
}
